import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Download, Shield, Zap } from 'lucide-react';
const included = ['4 shift models for every family situation', '20-Second Handoff Script (stops the fight before it starts)', 'Anti-resentment night rules — printed, followed, done', 'Weekly printable planner (7 nights, plug-and-play)', '5-minute pre-sleep setup checklist', 'Plan B protocol for regressions & illness', 'Bonus: copy-paste texts to start the conversation', 'Bonus: ON/OFF fridge card so nobody "forgets"'];
export function PricingSection() {
  return <section className="py-24 px-6 max-w-2xl mx-auto" id="pricing" data-id="element-143">
      <motion.div initial={{
      opacity: 0,
      y: 24
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.9
    }} className="text-center" data-id="element-144">
        {/* Pre-headline */}
        <p className="text-gold-400 uppercase tracking-[0.2em] text-xs font-semibold mb-4" data-id="element-145">
          Limited-time offer
        </p>

        <h2 className="font-serif text-4xl md:text-5xl text-cream-50 mb-4 leading-tight" data-id="element-146">
          Your first real sleep block
          <br data-id="element-147" />
          <span className="text-gold-400" data-id="element-148">starts tonight.</span>
        </h2>
        <p className="text-cream-100/60 mb-12 text-lg max-w-md mx-auto" data-id="element-149">
          You're not paying for pages. You're paying for the moment at 2:00am
          when you usually break.
        </p>

        {/* Product Image */}
        <div className="mb-10 relative mx-auto max-w-sm" data-id="element-150">
          <div className="absolute -inset-4 bg-gold-400/10 blur-2xl rounded-2xl opacity-60" data-id="element-151"></div>
          <img src="https://cdn.magicpatterns.com/uploads/iPFuVgmqLheiKQpZL8tKoK/heylloyd-image-1771281526452.png" alt="Sleep-Shifts Survival System Product" className="relative rounded-xl shadow-2xl w-full border border-white/10" data-id="element-152" />
        </div>

        {/* Pricing Card */}
        <div className="relative rounded-2xl overflow-hidden mb-8" data-id="element-153">
          {/* Top gold accent bar */}
          <div className="h-1 w-full bg-gradient-to-r from-gold-500 via-gold-400 to-gold-500" data-id="element-154" />

          <div className="bg-[#1a2540] border border-gold-400/20 border-t-0 rounded-b-2xl p-8 shadow-2xl" data-id="element-155">
            {/* Product name */}
            <h3 className="font-serif text-2xl text-cream-50 mb-6" data-id="element-156">
              Sleep-Shifts Survival System
            </h3>

            {/* Price block */}
            <div className="flex items-end justify-center gap-3 mb-2" data-id="element-157">
              <div className="text-center" data-id="element-158">
                <span className="block text-cream-100/35 line-through text-xl mb-0.5" data-id="element-159">
                  $37.00
                </span>
                <span className="text-6xl font-bold text-cream-50 tracking-tight" data-id="element-160">
                  $6.36
                </span>
              </div>
            </div>

            {/* Savings pill */}
            <div className="inline-flex items-center gap-2 bg-gold-400/15 border border-gold-400/25 text-gold-400 text-sm font-semibold px-4 py-1.5 rounded-full mb-8" data-id="element-161">
              <Zap className="w-3.5 h-3.5 fill-current" data-id="element-162" />
              You save $30.64 — 83% off today
            </div>

            {/* What's included mini-list */}
            <ul className="text-left space-y-3 mb-8" data-id="element-163">
              {included.map((item, i) => <li key={i} className="flex items-start gap-3 text-sm text-cream-100/80" data-id="element-164">
                  <Check className="w-4 h-4 text-gold-400 mt-0.5 flex-shrink-0" data-id="element-165" />
                  {item}
                </li>)}
            </ul>

            {/* CTA Button */}
            <motion.a href="https://www.betterbysteps.com/cart/44205947125853:1" target="_blank" rel="noopener noreferrer" animate={{
            boxShadow: ['0 0 16px #d4a85330', '0 0 32px #d4a85360', '0 0 16px #d4a85330']
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-full bg-gold-400 hover:bg-gold-500 text-[#0f1629] font-bold py-4 px-8 rounded-xl transition-colors duration-200 flex items-center justify-center gap-2 text-lg mb-4" data-id="element-166">
              Get Instant Access — $6.36
              <ArrowRight className="w-5 h-5" data-id="element-167" />
            </motion.a>

            {/* Guarantee */}
            <div className="flex items-center justify-center gap-2 text-cream-100/50 text-xs mb-5 py-3 border-t border-white/5 mt-2" data-id="element-168">
              <Shield className="w-3.5 h-3.5 text-gold-400/70" data-id="element-169" />
              <span data-id="element-170">
                If it doesn't help after 3 nights, reach out. We'll make it
                right.
              </span>
            </div>

            {/* Trust signals */}
            <div className="flex items-center justify-center gap-6 text-xs text-cream-100/40" data-id="element-171">
              <span className="flex items-center gap-1.5" data-id="element-172">
                <Download className="w-3.5 h-3.5" data-id="element-173" />
                Instant download
              </span>
              <span className="flex items-center gap-1.5" data-id="element-174">
                <Shield className="w-3.5 h-3.5" data-id="element-175" />
                Secure checkout
              </span>
              <span data-id="element-176">📱 Works on any device</span>
            </div>
          </div>
        </div>

        {/* Final push copy */}
        <div className="space-y-5 text-lg text-cream-50/75 pt-4" data-id="element-177">
          <p data-id="element-178">
            If you keep doing what you're doing, you already know what happens:
          </p>
          <p data-id="element-179">
            You'll make it through… and resent the person next to you for it.
          </p>
          <p className="font-serif text-2xl text-cream-50 pt-2" data-id="element-180">
            This is your exit from nightly negotiation.
          </p>
          <p className="text-sm text-cream-100/40 pt-6" data-id="element-181">
            Not a perfect baby. Not a perfect relationship.
            <br data-id="element-182" />A workable night system for real people who are running on
            nothing.
          </p>
        </div>
      </motion.div>
    </section>;
}